import { useState } from 'react';
import { EngraveSettings } from '../types';
import { GoogleFormService } from '../services/googleFormService';

interface GoogleFormSubmitProps {
  frontSettings: EngraveSettings | null;
  backSettings: EngraveSettings | null;
  canSubmit: boolean;
}

/**
 * Googleフォーム送信コンポーネント
 * 注文番号を入力して、Googleフォームに遷移
 */
export const GoogleFormSubmit: React.FC<GoogleFormSubmitProps> = ({ frontSettings, backSettings, canSubmit }) => {
  const [orderNo, setOrderNo] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [generatedUrl, setGeneratedUrl] = useState('');

  const formService = new GoogleFormService();

  const validate = (): boolean => {
    const errors: string[] = [];

    if (!orderNo || orderNo.trim().length === 0) {
      errors.push('注文番号を入力してください');
    } else if (!/^\d{8}$/.test(orderNo)) {
      errors.push('注文番号は8桁の数字で入力してください');
    }

    if (!frontSettings) {
      errors.push('彫刻設定が指定されていません');
    }

    // 前面または後面のいずれかにコンテンツがあるかチェック
    const hasFrontContent = frontSettings && (frontSettings.isImageMode ? !!frontSettings.imageData : (!!frontSettings.text && frontSettings.text.trim().length > 0));
    const hasBackContent = backSettings && (backSettings.isImageMode ? !!backSettings.imageData : (!!backSettings.text && backSettings.text.trim().length > 0));

    if (!hasFrontContent && !hasBackContent) {
      errors.push('前面と後面のどちらかを入力してください');
    }

    if (!canSubmit) {
      errors.push('プレビューを確認してから送信してください');
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = () => {
    setValidationErrors([]);

    if (!validate()) {
      return;
    }

    try {
      // GoogleフォームのURLを生成
      const url = formService.generatePrefilledUrl(orderNo, frontSettings!, backSettings);
      setGeneratedUrl(url);

      // 新しいタブでGoogleフォームを開く
      window.open(url, '_blank');
    } catch (error: any) {
      setValidationErrors([`エラーが発生しました: ${error.message}`]);
      console.error('Googleフォーム送信エラー:', error);
    }
  };

  return (
    <div className="google-form-submit-container">
      <p className="text-muted">プレビューを確認後、BOOTHの注文番号を入力してGoogleフォームで内容を送信してください。</p>

      <div className="form-group mb-3">
        <label htmlFor="orderNo" className="form-label">注文番号</label>
        <input
          type="text"
          id="orderNo"
          className="form-control"
          value={orderNo}
          onChange={(e) => setOrderNo(e.target.value)}
          placeholder="例: 12345678"
          maxLength={8}
          pattern="[0-9]{8}"
          inputMode="numeric"
        />
        <small className="form-text text-muted">8桁の数字を入力してください</small>
      </div>

      {validationErrors.length > 0 && (
        <div className="alert alert-warning">
          <ul className="mb-0 list-unstyled">
            {validationErrors.map((error, index) => (
              <li key={index}>
                <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="form-group mt-3">
        <button
          className="btn btn-success btn-lg w-100"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          <i className="bi bi-send"></i> Googleフォームを開く
        </button>
      </div>

      {generatedUrl && (
        <div className="alert alert-info mt-3">
          <strong>生成されたURL:</strong>
          <div className="small text-break">
            <a href={generatedUrl} target="_blank" rel="noopener noreferrer">{generatedUrl}</a>
          </div>
        </div>
      )}
    </div>
  );
};
